/**
 * Modern Calendar component with multiple views
 * Supports month, week, day, and list views
 * Following 2025 best practices with Next.js
 */

'use client';

import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, List, Grid3x3 } from 'lucide-react';
import type { EventTeaser, CalendarViewMode } from '../../types';
import { MonthView } from './month-view';
import { WeekView } from './week-view';
import { DayView } from './day-view';
import { EventsList } from '../events-list';

interface CalendarViewProps {
  /** Array of events to display */
  events: EventTeaser[];
  /** Initial view mode */
  initialView?: CalendarViewMode;
  /** Initial date to display */
  initialDate?: Date;
  /** Base URL for event links */
  baseUrl?: string;
}

/**
 * CalendarView component provides a full-featured calendar interface
 * with month, week, day, and list views
 */
export function CalendarView({
  events,
  initialView = 'month',
  initialDate = new Date(),
  baseUrl = '/events',
}: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [view, setView] = useState<CalendarViewMode>(initialView);

  // Navigation helpers
  const navigatePrevious = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      switch (view) {
        case 'month':
          newDate.setMonth(prev.getMonth() - 1);
          break;
        case 'week':
          newDate.setDate(prev.getDate() - 7);
          break;
        case 'day':
          newDate.setDate(prev.getDate() - 1);
          break;
      }
      return newDate;
    });
  };

  const navigateNext = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      switch (view) {
        case 'month':
          newDate.setMonth(prev.getMonth() + 1);
          break;
        case 'week':
          newDate.setDate(prev.getDate() + 7);
          break;
        case 'day':
          newDate.setDate(prev.getDate() + 1);
          break;
      }
      return newDate;
    });
  };

  const navigateToday = () => {
    setCurrentDate(new Date());
  };

  // Get current period label
  const periodLabel = useMemo(() => {
    switch (view) {
      case 'month':
        return currentDate.toLocaleDateString('en-GB', { 
          month: 'long', 
          year: 'numeric' 
        });
      case 'week': {
        const weekStart = new Date(currentDate);
        weekStart.setDate(currentDate.getDate() - currentDate.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return `${weekStart.toLocaleDateString('en-GB', { 
          day: 'numeric', 
          month: 'short' 
        })} - ${weekEnd.toLocaleDateString('en-GB', { 
          day: 'numeric', 
          month: 'short', 
          year: 'numeric' 
        })}`;
      }
      case 'day':
        return currentDate.toLocaleDateString('en-GB', { 
          weekday: 'long',
          day: 'numeric', 
          month: 'long', 
          year: 'numeric' 
        });
      case 'list':
        return 'All Events';
      default:
        return '';
    }
  }, [currentDate, view]);

  // Sort events by date for list view
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const dateA = a.eventDateTimeRange?.start?.timestamp ?? 0;
      const dateB = b.eventDateTimeRange?.start?.timestamp ?? 0;
      return dateA - dateB;
    });
  }, [events]);

  return (
    <div className="space-y-4">
      {/* Header with controls */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-centre sm:justify-between">
        {/* Navigation */}
        <div className="flex items-centre gap-2">
          <button
            onClick={navigateToday}
            className="rounded-lg border bg-background px-4 py-2 text-sm font-medium transition-colours hover:bg-accent"
          >
            Today
          </button>

          {view !== 'list' && (
            <>
              <button
                onClick={navigatePrevious}
                className="rounded-lg border bg-background p-2 transition-colours hover:bg-accent"
                aria-label="Previous"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={navigateNext}
                className="rounded-lg border bg-background p-2 transition-colours hover:bg-accent"
                aria-label="Next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}

          <h2 className="ml-2 text-lg font-semibold">{periodLabel}</h2>
        </div>

        {/* View switcher */}
        <div className="flex items-centre gap-1 rounded-lg border bg-background p-1">
          <button
            onClick={() => setView('month')}
            className={`flex items-centre gap-2 rounded px-3 py-1.5 text-sm font-medium transition-colours ${
              view === 'month'
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-accent'
            }`}
          >
            <CalendarIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Month</span>
          </button>
          <button
            onClick={() => setView('week')}
            className={`flex items-centre gap-2 rounded px-3 py-1.5 text-sm font-medium transition-colours ${
              view === 'week'
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-accent'
            }`}
          >
            <Grid3x3 className="h-4 w-4" />
            <span className="hidden sm:inline">Week</span>
          </button>
          <button
            onClick={() => setView('day')}
            className={`flex items-centre gap-2 rounded px-3 py-1.5 text-sm font-medium transition-colours ${
              view === 'day'
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-accent'
            }`}
          >
            <Grid3x3 className="h-4 w-4" />
            <span className="hidden sm:inline">Day</span>
          </button>
          <button
            onClick={() => setView('list')}
            className={`flex items-centre gap-2 rounded px-3 py-1.5 text-sm font-medium transition-colours ${
              view === 'list'
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-accent'
            }`}
          >
            <List className="h-4 w-4" />
            <span className="hidden sm:inline">List</span>
          </button>
        </div>
      </div>

      {/* Calendar view content */}
      <div className="rounded-lg border bg-card">
        {view === 'month' && (
          <MonthView 
            events={events} 
            currentDate={currentDate}
            baseUrl={baseUrl}
          />
        )}
        {view === 'week' && (
          <WeekView 
            events={events} 
            currentDate={currentDate}
            baseUrl={baseUrl}
          />
        )}
        {view === 'day' && (
          <DayView 
            events={events} 
            currentDate={currentDate}
            baseUrl={baseUrl}
          />
        )}
        {view === 'list' && (
          <div className="p-6">
            <EventsList 
              events={sortedEvents}
            />
          </div>
        )}
      </div>
    </div>
  );
}

